import { useState, useEffect, useCallback, useMemo } from 'react'
import { Upload, Image as ImageIcon, FileText, Film, Music, Grid, List, FolderOpen, Eye, Download, Trash2, MoreVertical, Building2, X, Search, ChevronLeft, ChevronRight, LayoutGrid, ImagePlus, ExternalLink, AlertTriangle, Calendar, HardDrive, Tag, Loader2, ChevronDown, Filter, Folder, FolderPlus, MoreHorizontal, Pencil, ChevronRight as ChevronRightIcon, Database, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { AssetUsageDialog, CreateImageElementDialog } from '@/components/assets'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { assetService, type AssetOData } from '@/lib/api/services/assets'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { FluentCard } from '@/components/layout/page-header'
import { Label } from '@/components/ui/label'
import {
  useFolderTree,
  useFolder,
  useCreateAssetFolder,
  useUpdateAssetFolder,
  useDeleteAssetFolder,
  type FolderTreeNode,
} from '@/lib/hooks'

const ASSET_TYPES = [
  { label: 'All Types', value: 'all', icon: FolderOpen, color: 'from-indigo-400 to-purple-600' },
  { label: 'Images', value: 'Image', icon: ImageIcon, color: 'from-blue-400 to-blue-600' },
  { label: 'Documents', value: 'Document', icon: FileText, color: 'from-emerald-400 to-green-600' },
  { label: 'Videos', value: 'Video', icon: Film, color: 'from-purple-400 to-purple-600' },
  { label: 'Audio', value: 'Audio', icon: Music, color: 'from-rose-400 to-rose-600' },
  { label: 'Spreadsheets', value: 'Spreadsheet', icon: FileText, color: 'from-green-400 to-green-600' },
  { label: 'Presentations', value: 'Presentation', icon: FileText, color: 'from-orange-400 to-orange-600' },
  { label: 'Archives', value: 'Archive', icon: FileText, color: 'from-gray-400 to-gray-600' },
  { label: 'Other', value: 'Other', icon: FileText, color: 'from-gray-400 to-gray-600' },
]

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96]

const assetTypeIcons = {
  Image: ImageIcon,
  Document: FileText,
  Video: Film,
  Audio: Music,
  Spreadsheet: FileText,
  Presentation: FileText,
  Archive: FileText,
  Other: FileText,
}

// MS-style folder colors palette
const MS_FOLDER_COLORS = [
  '#3B82F6', // blue-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#F59E0B', // amber-500
  '#10B981', // emerald-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#14B8A6', // teal-500
  '#6366F1', // indigo-500
] as const

// Helper to get initials from folder name
function getFolderInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length === 0 || !words[0]) return '??'
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase()
}

// Helper to get deterministic color from folder name (MS-style)
function getFolderColor(name: string, customColor?: string): string {
  if (customColor) return customColor
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return MS_FOLDER_COLORS[Math.abs(hash) % MS_FOLDER_COLORS.length] ?? '#6366F1'
}

// Helper to get background color style
function getColorStyle(folderName: string, customColor?: string): React.CSSProperties {
  return { backgroundColor: getFolderColor(folderName, customColor) }
}

// ============================================
// Folder Tree Item Component
// ============================================

interface FolderTreeItemProps {
  folder: FolderTreeNode
  level: number
  selectedFolderId: string | null
  expandedIds: Set<string>
  onSelect: (folderId: string | null) => void
  onToggleExpand: (folderId: string) => void
  onEdit: (folder: FolderTreeNode) => void
  onDelete: (folder: FolderTreeNode) => void
  onAddSubfolder: (parentId: string) => void
}

function FolderTreeItem({
  folder,
  level,
  selectedFolderId,
  expandedIds,
  onSelect,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddSubfolder,
}: FolderTreeItemProps) {
  const isExpanded = expandedIds.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const hasChildren = folder.children && folder.children.length > 0

  return (
    <div className="group">
      <div
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          isSelected && 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-medium'
        )}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(folder.id)
            }}
            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Folder Icon */}
        <button
          onClick={() => onSelect(folder.id)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <div
            className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
            style={getColorStyle(folder.name, folder.color)}
          >
            {getFolderInitials(folder.name)}
          </div>
          <span className="truncate flex-1 text-left">{folder.name}</span>
        </button>

        {/* Asset Count */}
        <span className="text-xs text-gray-400 tabular-nums mr-1">{folder.assetCount || 0}</span>

        {/* Context Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity"
            >
              <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onAddSubfolder(folder.id)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Subfolder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(folder)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(folder)}
              className="text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children?.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSubfolder={onAddSubfolder}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================
// Folder Dialog State Types
// ============================================

interface FolderDialogState {
  open: boolean
  mode: 'create' | 'edit'
  parentId?: string
  folder?: FolderTreeNode
}

interface DeleteFolderDialogState {
  open: boolean
  folder: FolderTreeNode | null
}

export default function Assets() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const [tenantName, setTenantName] = useState<string | null>(null)

  const [assets, setAssets] = useState<AssetOData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [typePickerOpen, setTypePickerOpen] = useState(false)
  const { toast } = useToast()

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sidebarSearch, setSidebarSearch] = useState('')

  // Folder state
  const selectedFolderId = searchParams.get('folder')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [folderDialog, setFolderDialog] = useState<FolderDialogState>({
    open: false,
    mode: 'create',
  })
  const [folderName, setFolderName] = useState('')
  const [folderDescription, setFolderDescription] = useState('')
  const [deleteFolderDialog, setDeleteFolderDialog] = useState<DeleteFolderDialogState>({
    open: false,
    folder: null,
  })

  // Folder data
  const { data: folderTree = [], isLoading: foldersLoading } = useFolderTree()
  const { data: selectedFolder } = useFolder(selectedFolderId || '', true, {
    enabled: !!selectedFolderId,
  })

  // Folder mutations
  const createFolderMutation = useCreateAssetFolder({
    onSuccess: () => {
      toast({ title: 'Folder created', description: 'The folder has been created successfully.' })
      setFolderDialog({ open: false, mode: 'create' })
      setFolderName('')
      setFolderDescription('')
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const updateFolderMutation = useUpdateAssetFolder({
    onSuccess: () => {
      toast({ title: 'Folder updated', description: 'The folder has been updated successfully.' })
      setFolderDialog({ open: false, mode: 'create' })
      setFolderName('')
      setFolderDescription('')
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const deleteFolderMutation = useDeleteAssetFolder({
    onSuccess: () => {
      toast({ title: 'Folder deleted', description: 'The folder has been deleted successfully.' })
      setDeleteFolderDialog({ open: false, folder: null })
      if (selectedFolderId === deleteFolderDialog.folder?.id) {
        handleSelectFolder(null)
      }
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  // Dialog states for Content Hub integration
  const [usageDialogOpen, setUsageDialogOpen] = useState(false)
  const [createImageDialogOpen, setCreateImageDialogOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<AssetOData | null>(null)

  // Dialog states for View and Delete
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [assetToDelete, setAssetToDelete] = useState<AssetOData | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  // Clear tenant filter
  const clearTenantFilter = () => {
    searchParams.delete('tenantId')
    searchParams.delete('assetId')
    setSearchParams(searchParams)
    setTenantName(null)
  }

  // Load tenant name when filtering by tenant
  useEffect(() => {
    const loadTenantName = async () => {
      if (tenantId) {
        try {
          const { tenantsService } = await import('@/lib/api/services/tenants')
          const tenant = await tenantsService.getTenant(tenantId)
          setTenantName(tenant.name)
        } catch {
          // Ignore error, just won't show tenant name
        }
      }
    }
    loadTenantName()
  }, [tenantId])

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = getODataParams()

      // Add tenant filter if tenantId is provided
      if (tenantId) {
        const tenantFilter = { field: 'tenantId', operator: 'eq' as const, value: tenantId }
        params.filter = params.filter
          ? [...params.filter, tenantFilter]
          : [tenantFilter]
      }

      // Add type filter if not 'all'
      if (typeFilter && typeFilter !== 'all') {
        const assetTypeFilter = { field: 'type', operator: 'eq' as const, value: typeFilter }
        params.filter = params.filter
          ? [...params.filter, assetTypeFilter]
          : [assetTypeFilter]
      }

      // Add folder filter if selected
      if (selectedFolderId) {
        const folderFilter = { field: 'folderId', operator: 'eq' as const, value: selectedFolderId }
        params.filter = params.filter
          ? [...params.filter, folderFilter]
          : [folderFilter]
      }

      const response = await assetService.getAssets(params)
      setAssets(response.value || [])
      setTotalCount(response['@odata.count'] || response.value?.length || 0)
    } catch (_error) {
      setError('Failed to load assets')
      toast({
        title: 'Error',
        description: 'Failed to load assets',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast, tenantId, typeFilter, selectedFolderId])

  useEffect(() => {
    loadAssets()
  }, [loadAssets])

  const handleView = (asset: AssetOData) => {
    setSelectedAsset(asset)
    setPreviewDialogOpen(true)
  }

  const handleOpenInNewTab = (asset: AssetOData) => {
    if (asset.url) {
      window.open(asset.url, '_blank')
    } else {
      toast({
        title: 'Error',
        description: 'Asset URL not available',
        variant: 'destructive',
      })
    }
  }

  const handleDownload = async (asset: AssetOData) => {
    setIsDownloading(true)
    try {
      const blob = await assetService.downloadAsset(asset.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = asset.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast({
        title: 'Success',
        description: 'Download started',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to download asset',
        variant: 'destructive',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDeleteClick = (asset: AssetOData) => {
    setAssetToDelete(asset)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!assetToDelete) return

    setIsDeleting(true)
    try {
      await assetService.deleteAsset(assetToDelete.id)
      toast({
        title: 'Success',
        description: 'Asset deleted successfully',
      })
      setDeleteDialogOpen(false)
      setAssetToDelete(null)
      loadAssets()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete asset',
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Open usage dialog for an asset
  const handleViewUsage = (asset: AssetOData) => {
    setSelectedAsset(asset)
    setUsageDialogOpen(true)
  }

  // Open create image element dialog for an asset
  const handleCreateImageElement = (asset: AssetOData) => {
    setSelectedAsset(asset)
    setCreateImageDialogOpen(true)
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const uploadPromises = Array.from(files).map(file =>
        assetService.uploadAsset({ file, folderId: selectedFolderId || undefined })
      )
      await Promise.all(uploadPromises)
      toast({
        title: 'Success',
        description: `${files.length} asset(s) uploaded successfully`,
      })
      loadAssets()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to upload assets',
        variant: 'destructive',
      })
    }
  }

  const getAssetIcon = (type: string) => {
    const Icon = assetTypeIcons[type as keyof typeof assetTypeIcons] || FileText
    return <Icon className="h-5 w-5 text-white" />
  }

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB'
  }

  // Handle type filter change
  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value)
    setPage(1)
    setTypePickerOpen(false)
  }

  // Get current type label
  const getCurrentTypeLabel = () => {
    const type = ASSET_TYPES.find(t => t.value === typeFilter)
    return type?.label || 'All Types'
  }

  // Folder handlers
  const handleSelectFolder = (folderId: string | null) => {
    if (folderId) {
      searchParams.set('folder', folderId)
    } else {
      searchParams.delete('folder')
    }
    setSearchParams(searchParams)
    setPage(1)
  }

  const handleToggleExpand = (folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  const handleOpenCreateFolder = (parentId?: string) => {
    setFolderDialog({ open: true, mode: 'create', parentId })
    setFolderName('')
    setFolderDescription('')
  }

  const handleOpenEditFolder = (folder: FolderTreeNode) => {
    setFolderDialog({ open: true, mode: 'edit', folder })
    setFolderName(folder.name)
    setFolderDescription(folder.description || '')
  }

  const handleFolderSubmit = async () => {
    if (!folderName.trim()) return
    if (folderDialog.mode === 'create') {
      await createFolderMutation.mutateAsync({
        name: folderName,
        description: folderDescription || undefined,
        parentId: folderDialog.parentId,
      })
    } else if (folderDialog.folder) {
      await updateFolderMutation.mutateAsync({
        id: folderDialog.folder.id,
        data: { name: folderName, description: folderDescription || undefined },
      })
    }
  }

  const handleDeleteFolder = async () => {
    if (!deleteFolderDialog.folder) return
    await deleteFolderMutation.mutateAsync({ id: deleteFolderDialog.folder.id })
  }

  // Auto-expand first level folders
  useEffect(() => {
    if (folderTree.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(folderTree.map((f) => f.id)))
    }
  }, [folderTree])

  // Calculate total assets in folders
  const totalAssetsInFolders = useMemo(() => {
    const countAll = (folders: FolderTreeNode[]): number =>
      folders.reduce((sum, folder) => sum + (folder.assetCount || 0) + countAll(folder.children || []), 0)
    return countAll(folderTree)
  }, [folderTree])

  // Filter folders by search
  const filteredFolders = useMemo(() => {
    if (!sidebarSearch.trim()) return folderTree
    const searchTerm = sidebarSearch.toLowerCase()
    const filterTree = (folders: FolderTreeNode[]): FolderTreeNode[] => {
      return folders
        .map((folder) => ({
          ...folder,
          children: folder.children ? filterTree(folder.children) : [],
        }))
        .filter(
          (folder) =>
            folder.name.toLowerCase().includes(searchTerm) ||
            folder.description?.toLowerCase().includes(searchTerm) ||
            (folder.children && folder.children.length > 0)
        )
    }
    return filterTree(folderTree)
  }, [folderTree, sidebarSearch])

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize)
  const startItem = (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, totalCount)

  const columns: ColumnDef<AssetOData>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (asset) => (
        <div className="flex items-center gap-3">
          {asset.type === 'Image' && asset.thumbnailUrl ? (
            <div className="relative h-10 w-10 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img
                src={asset.thumbnailUrl}
                alt={asset.name}
                loading="lazy"
                className="object-cover w-full h-full"
                onError={(e) => {
                  const target = e.currentTarget
                  target.style.display = 'none'
                  const placeholder = target.nextElementSibling as HTMLElement
                  if (placeholder) placeholder.style.display = 'flex'
                }}
              />
              <div className="absolute inset-0 items-center justify-center bg-gradient-to-br from-emerald-400 to-green-600 hidden">
                <ImageIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          ) : (
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center flex-shrink-0">
              {getAssetIcon(asset.type)}
            </div>
          )}
          <span className="font-medium text-gray-900 dark:text-white truncate">{asset.name}</span>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Image', value: 'Image' },
        { label: 'Document', value: 'Document' },
        { label: 'Video', value: 'Video' },
        { label: 'Audio', value: 'Audio' },
        { label: 'Spreadsheet', value: 'Spreadsheet' },
        { label: 'Presentation', value: 'Presentation' },
        { label: 'Archive', value: 'Archive' },
        { label: 'Other', value: 'Other' },
      ],
      cell: (asset) => (
        <Badge variant="secondary">{asset.type}</Badge>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      accessor: 'size',
      sortable: true,
      cell: (asset) => (
        <span className="text-gray-500 dark:text-gray-400">
          {formatFileSize(asset.size)}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Uploaded',
      accessor: 'createdAt',
      sortable: true,
      cell: (asset) => (
        <span className="text-gray-500 dark:text-gray-400">
          {new Date(asset.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '100px',
      cell: (asset) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleView(asset)}>
              <Eye className="h-4 w-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleDownload(asset)}>
              <Download className="h-4 w-4" />
              Download
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleViewUsage(asset)}>
              <LayoutGrid className="h-4 w-4" />
              View Content Hub Usage
            </DropdownMenuItem>
            {asset.type === 'Image' && (
              <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleCreateImageElement(asset)}>
                <ImagePlus className="h-4 w-4" />
                Create Image Element
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <PermissionGate permissions={['assets.delete']}>
              <DropdownMenuItem
                className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                onClick={() => handleDeleteClick(asset)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  const renderGridPagination = () => {
    if (totalCount === 0) return null

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 px-2">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing {startItem} - {endItem} of {totalCount} assets
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="rounded-lg"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className="rounded-lg w-9 h-9 p-0"
                >
                  {pageNum}
                </Button>
              )
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="rounded-lg"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    )
  }

  const renderGridView = () => {
    if (loading) {
      return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-none" />
          ))}
        </div>
      )
    }

    if (assets.length === 0) {
      return (
        <div className="text-center py-12">
          <Upload className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            No assets found
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {search || typeFilter !== 'all' || selectedFolderId
              ? 'Try adjusting your search or filters'
              : 'Get started by uploading your first asset'}
          </p>
          {(search || typeFilter !== 'all' || selectedFolderId) && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 rounded-xl"
              onClick={() => {
                setSearch('')
                setTypeFilter('all')
                handleSelectFolder(null)
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {assets.map((asset) => (
            <FluentCard key={asset.id} hover className="overflow-hidden p-0">
              <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center relative">
                {asset.type === 'Image' && asset.url ? (
                  <>
                    <img
                      src={asset.url}
                      alt={asset.name}
                      loading="lazy"
                      className="object-cover w-full h-full absolute inset-0"
                      onError={(e) => {
                        const target = e.currentTarget
                        target.style.display = 'none'
                        const placeholder = target.nextElementSibling as HTMLElement
                        if (placeholder) placeholder.style.display = 'flex'
                      }}
                    />
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-600 hidden items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-white" />
                    </div>
                  </>
                ) : (
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-600">
                    {getAssetIcon(asset.type)}
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-medium text-gray-900 dark:text-white truncate">{asset.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {formatFileSize(asset.size)} • {new Date(asset.createdAt).toLocaleDateString()}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {asset.type}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                        <MoreVertical className="h-5 w-5 text-gray-400" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 rounded-xl">
                      <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleView(asset)}>
                        <Eye className="h-4 w-4" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleDownload(asset)}>
                        <Download className="h-4 w-4" />
                        Download
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleViewUsage(asset)}>
                        <LayoutGrid className="h-4 w-4" />
                        View Content Hub Usage
                      </DropdownMenuItem>
                      {asset.type === 'Image' && (
                        <DropdownMenuItem className="rounded-lg gap-2" onClick={() => handleCreateImageElement(asset)}>
                          <ImagePlus className="h-4 w-4" />
                          Create Image Element
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <PermissionGate permissions={['assets.delete']}>
                        <DropdownMenuItem
                          className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                          onClick={() => handleDeleteClick(asset)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </PermissionGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </FluentCard>
          ))}
        </div>
        {renderGridPagination()}
      </>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6">
      {/* Sidebar - Folder Tree */}
      <div
        className={cn(
          'border-r border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col transition-all duration-300',
          sidebarOpen ? 'w-72' : 'w-0'
        )}
      >
        {sidebarOpen && (
          <>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Folder className="h-4 w-4 text-emerald-500" />
                  Folders
                </h2>
                <PermissionGate permissions={['assets.create']}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleOpenCreateFolder()}
                    title="Create Folder"
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </PermissionGate>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search folders..."
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>

            {/* Sidebar Content */}
            <ScrollArea className="flex-1">
              <div className="p-2">
                {/* All Assets */}
                <button
                  onClick={() => handleSelectFolder(null)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all mb-1',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    !selectedFolderId &&
                      'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-medium'
                  )}
                >
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                    <Database className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="flex-1 text-left">All Assets</span>
                  <span className="text-xs text-gray-400 tabular-nums">{totalAssetsInFolders || totalCount}</span>
                </button>

                {/* Divider */}
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />

                {/* Folder Tree */}
                {foldersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredFolders.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {sidebarSearch ? 'No folders found' : 'No folders yet'}
                    </p>
                    {!sidebarSearch && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleOpenCreateFolder()}
                        className="mt-1"
                      >
                        Create your first folder
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredFolders.map((folder) => (
                      <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        level={0}
                        selectedFolderId={selectedFolderId}
                        expandedIds={expandedIds}
                        onSelect={handleSelectFolder}
                        onToggleExpand={handleToggleExpand}
                        onEdit={handleOpenEditFolder}
                        onDelete={(f) => setDeleteFolderDialog({ open: true, folder: f })}
                        onAddSubfolder={handleOpenCreateFolder}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          {/* Tenant Filter Banner */}
          {tenantId && (
            <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 mb-6">
              <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  Showing assets for company:{' '}
                  <Link
                    to={`/admin/companies/${tenantId}`}
                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {tenantName || tenantId}
                  </Link>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearTenantFilter}
                  className="h-8 px-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filter
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              {sidebarOpen ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </Button>
            <div className="flex-1">
              {/* Custom Header with Folder Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedFolder && (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-lg"
                      style={getColorStyle(selectedFolder.name, selectedFolder.color)}
                    >
                      {getFolderInitials(selectedFolder.name)}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedFolder ? selectedFolder.name : 'Asset Library'}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {selectedFolder
                        ? selectedFolder.description || `${selectedFolder.assetCount || 0} assets in this folder`
                        : 'Manage your digital assets and media files'}
                    </p>
                  </div>
                </div>
                <PermissionGate permissions={['assets.create']}>
                  <div>
                    <input
                      type="file"
                      id="asset-upload"
                      className="hidden"
                      multiple
                      onChange={handleUpload}
                    />
                    <label
                      htmlFor="asset-upload"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20 cursor-pointer"
                    >
                      <Upload className="h-4 w-4" />
                      Upload Assets
                    </label>
                  </div>
                </PermissionGate>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 sm:grid-cols-4 mb-6">
            {[
              { label: 'Total Assets', value: totalCount, color: 'from-emerald-400 to-green-600', icon: FolderOpen },
              { label: 'Images', value: assets.filter(a => a.type === 'Image').length, color: 'from-blue-400 to-blue-600', icon: ImageIcon },
              { label: 'Documents', value: assets.filter(a => a.type === 'Document').length, color: 'from-violet-400 to-purple-600', icon: FileText },
              { label: 'Other', value: assets.filter(a => !['Image', 'Document'].includes(a.type)).length, color: 'from-gray-400 to-gray-600', icon: Film },
            ].map((stat) => (
              <FluentCard key={stat.label} className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </FluentCard>
            ))}
          </div>
        </div>

        {/* Assets Content */}
        <div className="flex-1 overflow-hidden px-6 pb-6">
          <FluentCard className="h-full overflow-hidden flex flex-col">
            {/* Toolbar */}
            <div className="flex flex-col gap-3 p-4 border-b border-gray-200 dark:border-gray-800">
              {/* Top row: Filters and Search */}
              <div className="flex items-center gap-3">
                {/* Type Filter Popover */}
                <Popover open={typePickerOpen} onOpenChange={setTypePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Filter className="h-4 w-4" />
                      {getCurrentTypeLabel()}
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium">Filter by Type</span>
                    </div>
                    <ScrollArea className="h-80">
                      <div className="p-2">
                        {ASSET_TYPES.map((type) => {
                          const Icon = type.icon
                          const isSelected = typeFilter === type.value
                          const count = type.value === 'all'
                            ? totalCount
                            : assets.filter(a => a.type === type.value).length
                          return (
                            <button
                              key={type.value}
                              onClick={() => handleTypeFilterChange(type.value)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all mb-1',
                                'hover:bg-gray-100 dark:hover:bg-gray-800',
                                isSelected && 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-medium'
                              )}
                            >
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center`}>
                                <Icon className="h-4 w-4 text-white" />
                              </div>
                              <span className="flex-1 text-left">{type.label}</span>
                              <span className="text-xs text-gray-400 tabular-nums">{count}</span>
                            </button>
                          )
                        })}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>

                {/* Search */}
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search assets..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setPage(1)
                    }}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Bottom row: Active filters, results count, page size, view toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Active type filter badge */}
                  {typeFilter !== 'all' && (
                    <Badge
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                    >
                      {getCurrentTypeLabel()}
                      <button
                        onClick={() => handleTypeFilterChange('all')}
                        className="ml-1 rounded-full p-0.5 hover:bg-indigo-200 dark:hover:bg-indigo-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {/* Active search badge */}
                  {search && (
                    <Badge
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    >
                      Search: "{search}"
                      <button
                        onClick={() => { setSearch(''); setPage(1); }}
                        className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {/* Clear all */}
                  {(typeFilter !== 'all' || search) && (
                    <button
                      onClick={() => {
                        setTypeFilter('all')
                        setSearch('')
                        setPage(1)
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
                    >
                      Clear all
                    </button>
                  )}
                  {/* Results count */}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {totalCount} {totalCount === 1 ? 'asset' : 'assets'}
                    {(typeFilter !== 'all' || search) && ' found'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Page Size - Grid View Only */}
                  {viewMode === 'grid' && (
                    <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                      <SelectTrigger className="w-[100px] rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={size.toString()}>
                            {size} / page
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        'p-2 rounded-md transition-colors',
                        viewMode === 'grid'
                          ? 'bg-white dark:bg-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      )}
                      title="Grid view"
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={cn(
                        'p-2 rounded-md transition-colors',
                        viewMode === 'list'
                          ? 'bg-white dark:bg-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      )}
                      title="List view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {viewMode === 'list' ? (
                <DataTable
                  columns={columns}
                  data={assets}
                  totalCount={totalCount}
                  loading={loading}
                  error={error}
                  onRetry={loadAssets}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSortChange={setSort}
                  filters={filters}
                  onFilterChange={setFilters}
                  rowKey="id"
                  emptyMessage="No assets found"
                  hideToolbar
                />
              ) : (
                renderGridView()
              )}
            </div>
          </FluentCard>
        </div>
      </div>

      {/* Content Hub Integration Dialogs */}
      <AssetUsageDialog
        open={usageDialogOpen}
        onOpenChange={setUsageDialogOpen}
        assetId={selectedAsset?.id || ''}
        assetName={selectedAsset?.name || ''}
        assetType={selectedAsset?.type || 'Image'}
      />

      <CreateImageElementDialog
        open={createImageDialogOpen}
        onOpenChange={setCreateImageDialogOpen}
        asset={selectedAsset}
      />

      {/* Asset Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAsset && getAssetIcon(selectedAsset.type)}
              <span className="truncate">{selectedAsset?.name}</span>
            </DialogTitle>
            <DialogDescription>
              View asset details and preview
            </DialogDescription>
          </DialogHeader>

          {selectedAsset && (
            <div className="space-y-4">
              {/* Asset Preview */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden flex items-center justify-center min-h-[300px] max-h-[400px]">
                {selectedAsset.type === 'Image' && selectedAsset.url ? (
                  <img
                    src={selectedAsset.url}
                    alt={selectedAsset.name}
                    className="max-w-full max-h-[400px] object-contain"
                    onError={(e) => {
                      const target = e.currentTarget
                      target.style.display = 'none'
                      const placeholder = target.nextElementSibling as HTMLElement
                      if (placeholder) placeholder.style.display = 'flex'
                    }}
                  />
                ) : selectedAsset.type === 'Video' && selectedAsset.url ? (
                  <video
                    src={selectedAsset.url}
                    controls
                    className="max-w-full max-h-[400px]"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : selectedAsset.type === 'Audio' && selectedAsset.url ? (
                  <div className="p-8 flex flex-col items-center gap-4">
                    <div className="p-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600">
                      <Music className="h-12 w-12 text-white" />
                    </div>
                    <audio src={selectedAsset.url} controls className="w-full max-w-md">
                      Your browser does not support the audio tag.
                    </audio>
                  </div>
                ) : (
                  <div className="p-8 flex flex-col items-center gap-4">
                    <div className="p-6 rounded-full bg-gradient-to-br from-emerald-400 to-green-600">
                      {getAssetIcon(selectedAsset.type)}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Preview not available for this file type
                    </p>
                  </div>
                )}
              </div>

              {/* Asset Details */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Type</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedAsset.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Size</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formatFileSize(selectedAsset.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Uploaded</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(selectedAsset.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                {selectedAsset.tags && selectedAsset.tags.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Tags</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {selectedAsset.tags.join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => selectedAsset && handleOpenInNewTab(selectedAsset)}
              className="rounded-xl"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
            <Button
              onClick={() => selectedAsset && handleDownload(selectedAsset)}
              disabled={isDownloading}
              className="rounded-xl"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Asset
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{assetToDelete?.name}"? This action cannot be undone and will permanently remove the asset from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {assetToDelete && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              {assetToDelete.type === 'Image' && assetToDelete.thumbnailUrl ? (
                <img
                  src={assetToDelete.thumbnailUrl}
                  alt={assetToDelete.name}
                  className="h-12 w-12 rounded-lg object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center">
                  {getAssetIcon(assetToDelete.type)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{assetToDelete.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {assetToDelete.type} • {formatFileSize(assetToDelete.size)}
                </p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700 rounded-xl"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Asset
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Folder Dialog */}
      <Dialog open={folderDialog.open} onOpenChange={(open) => !open && setFolderDialog({ open: false, mode: 'create' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{folderDialog.mode === 'create' ? 'Create Folder' : 'Edit Folder'}</DialogTitle>
            <DialogDescription>
              {folderDialog.mode === 'create'
                ? 'Create a new folder to organize your assets.'
                : 'Update the folder details.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Name</Label>
              <Input
                id="folder-name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Enter folder name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder-description">Description (optional)</Label>
              <Input
                id="folder-description"
                value={folderDescription}
                onChange={(e) => setFolderDescription(e.target.value)}
                placeholder="Enter folder description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog({ open: false, mode: 'create' })}>
              Cancel
            </Button>
            <Button
              onClick={handleFolderSubmit}
              disabled={!folderName.trim() || createFolderMutation.isPending || updateFolderMutation.isPending}
            >
              {createFolderMutation.isPending || updateFolderMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Dialog */}
      <AlertDialog
        open={deleteFolderDialog.open}
        onOpenChange={(open) => !open && setDeleteFolderDialog({ open: false, folder: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Folder
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteFolderDialog.folder?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteFolder}
              disabled={deleteFolderMutation.isPending}
            >
              {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
